import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  Paper,
  Collapse,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Quiz Types based on the structured JSON from n8n
export interface QuizOption {
  id: string;
  text: string;
}

export interface QuizQuestion {
  id: number;
  typ: 'single_choice' | 'multiple_choice' | 'richtig_falsch' | 'szenario' | 'multiple_select';
  schwierigkeit: 'grundwissen' | 'anwendung' | 'analyse';
  lernziel_referenz: number | string;
  frage?: string;
  aussage?: string; // For richtig_falsch questions
  szenario?: string; // For scenario questions
  kontext?: string; // Alternative for scenario context
  optionen?: QuizOption[];
  richtige_antwort?: string | boolean; // Single ID or boolean
  richtige_antworten?: string[]; // Array of IDs for multiple_select
  erklaerung: string;
}

export interface Quiz {
  titel: string;
  beschreibung?: string;
  bestehensgrenze?: number; // Percentage to pass, default 70
  zeitlimit_minuten?: number;
  fragen: QuizQuestion[];
}

// Flexible Schulung interface to handle various AI response formats
export interface Lernziel {
  id: number | string;
  beschreibung: string;
  niveau?: string;
  messbar_durch?: string;
}

export interface Modul {
  nr?: number;
  nummer?: number;
  name?: string;
  titel?: string;
  dauer: string;
  themen?: string[];
  inhalte?: string[];
  methode?: string;
  uebung?: {
    titel: string;
    szenario?: string;
    aufgabe?: string;
    erwartetes_ergebnis?: string;
  };
  praxisuebung?: string;
}

export interface Schulung {
  titel: string;
  zielgruppe?: string;
  gesamtdauer?: string;
  lernziele: Lernziel[];
  module: Modul[];
  materialien?: {
    praesentationen?: string[];
    handouts?: string[];
    software?: string[];
    ressourcen?: string[];
  };
  assessment?: {
    methode: string;
    kriterien: string[];
  };
}

export interface TrainingContent {
  schulung: Schulung;
  quiz: Quiz;
}

// Difficulty badge colors
const difficultyColors: Record<string, 'success' | 'warning' | 'error'> = {
  grundwissen: 'success',
  anwendung: 'warning',
  analyse: 'error',
};

const difficultyLabels: Record<string, string> = {
  grundwissen: 'Grundwissen',
  anwendung: 'Anwendung',
  analyse: 'Analyse',
};

interface QuizComponentProps {
  quiz: Quiz;
  onComplete?: (score: number, total: number) => void;
}

export const QuizComponent: React.FC<QuizComponentProps> = ({ quiz, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[] | boolean>>({});
  const [submitted, setSubmitted] = useState<Record<number, boolean>>({});
  const [showExplanation, setShowExplanation] = useState<Record<number, boolean>>({});
  const [quizCompleted, setQuizCompleted] = useState(false);

  const currentQuestion = quiz.fragen[currentIndex];
  const totalQuestions = quiz.fragen.length;
  const passThreshold = quiz.bestehensgrenze || 70;

  // Helper to get correct answer(s) for a question
  const getCorrectAnswer = (q: QuizQuestion): string | string[] | boolean => {
    if (q.typ === 'multiple_select' || (q.typ === 'multiple_choice' && q.richtige_antworten)) {
      return q.richtige_antworten || [];
    }
    if (q.typ === 'richtig_falsch') {
      return q.richtige_antwort as boolean;
    }
    return q.richtige_antwort as string;
  };

  // Helper to check if question uses multiple selection
  const isMultiSelect = (q: QuizQuestion): boolean => {
    return q.typ === 'multiple_select' || (q.typ === 'multiple_choice' && Array.isArray(q.richtige_antworten));
  };

  // Helper to get question text
  const getQuestionText = (q: QuizQuestion): string => {
    return q.frage || q.aussage || '';
  };

  // Calculate score
  const score = useMemo(() => {
    let correct = 0;
    quiz.fragen.forEach((q) => {
      if (submitted[q.id]) {
        const userAnswer = answers[q.id];
        const correctAnswer = getCorrectAnswer(q);
        
        if (q.typ === 'richtig_falsch') {
          // Boolean comparison for true/false questions
          if (userAnswer === correctAnswer || 
              (userAnswer === 'true' && correctAnswer === true) ||
              (userAnswer === 'false' && correctAnswer === false)) {
            correct++;
          }
        } else if (Array.isArray(correctAnswer)) {
          // Multiple select - check if arrays match
          if (Array.isArray(userAnswer)) {
            const sortedUser = [...userAnswer].sort();
            const sortedCorrect = [...correctAnswer].sort();
            if (JSON.stringify(sortedUser) === JSON.stringify(sortedCorrect)) {
              correct++;
            }
          }
        } else {
          // Single choice
          if (userAnswer === correctAnswer) {
            correct++;
          }
        }
      }
    });
    return correct;
  }, [answers, submitted, quiz.fragen]);

  const answeredCount = Object.keys(submitted).length;
  const progress = (answeredCount / totalQuestions) * 100;

  const handleSingleAnswer = (questionId: number, value: string) => {
    if (!submitted[questionId]) {
      setAnswers(prev => ({ ...prev, [questionId]: value }));
    }
  };

  const handleMultipleAnswer = (questionId: number, optionId: string, checked: boolean) => {
    if (!submitted[questionId]) {
      setAnswers(prev => {
        const current = (prev[questionId] as string[]) || [];
        if (checked) {
          return { ...prev, [questionId]: [...current, optionId] };
        } else {
          return { ...prev, [questionId]: current.filter(id => id !== optionId) };
        }
      });
    }
  };

  const handleSubmitAnswer = () => {
    if (answers[currentQuestion.id] !== undefined) {
      setSubmitted(prev => ({ ...prev, [currentQuestion.id]: true }));
      setShowExplanation(prev => ({ ...prev, [currentQuestion.id]: true }));
    }
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleFinishQuiz = () => {
    setQuizCompleted(true);
    if (onComplete) {
      onComplete(score, totalQuestions);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setAnswers({});
    setSubmitted({});
    setShowExplanation({});
    setQuizCompleted(false);
  };

  const isCorrect = (questionId: number): boolean => {
    const userAnswer = answers[questionId];
    const question = quiz.fragen.find(q => q.id === questionId);
    if (!question) return false;

    const correctAnswer = getCorrectAnswer(question);
    
    if (question.typ === 'richtig_falsch') {
      return userAnswer === correctAnswer || 
             (userAnswer === 'true' && correctAnswer === true) ||
             (userAnswer === 'false' && correctAnswer === false);
    }
    
    if (Array.isArray(correctAnswer)) {
      if (Array.isArray(userAnswer)) {
        const sortedUser = [...userAnswer].sort();
        const sortedCorrect = [...correctAnswer].sort();
        return JSON.stringify(sortedUser) === JSON.stringify(sortedCorrect);
      }
      return false;
    }
    return userAnswer === correctAnswer;
  };

  // Results screen
  if (quizCompleted) {
    const percentage = Math.round((score / totalQuestions) * 100);
    const passed = percentage >= passThreshold;

    return (
      <Card sx={{ 
        background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
        border: '1px solid rgba(0, 180, 216, 0.3)',
      }}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h4" gutterBottom>
              Quiz abgeschlossen!
            </Typography>
            
            <Box sx={{ my: 4 }}>
              <Typography variant="h2" sx={{ 
                color: passed ? 'success.main' : 'error.main',
                fontWeight: 700,
              }}>
                {percentage}%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {score} von {totalQuestions} Fragen richtig
              </Typography>
            </Box>

            <Alert 
              severity={passed ? 'success' : 'warning'} 
              sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}
            >
              {passed 
                ? '🎉 Herzlichen Glückwunsch! Sie haben das Quiz bestanden.'
                : `Sie benötigen mindestens ${passThreshold}% zum Bestehen. Versuchen Sie es erneut!`
              }
            </Alert>

            <Button 
              variant="contained" 
              startIcon={<RestartAltIcon />}
              onClick={handleRestart}
              size="large"
            >
              Quiz wiederholen
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Question summary */}
          <Typography variant="h6" gutterBottom>
            Übersicht aller Fragen
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            {quiz.fragen.map((q, idx) => (
              <Chip
                key={q.id}
                label={`${idx + 1}`}
                icon={isCorrect(q.id) ? <CheckCircleIcon /> : <CancelIcon />}
                color={isCorrect(q.id) ? 'success' : 'error'}
                onClick={() => {
                  setQuizCompleted(false);
                  setCurrentIndex(idx);
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.95), rgba(36, 59, 83, 0.95))',
      border: '1px solid rgba(0, 180, 216, 0.3)',
    }}>
      <CardContent>
        {/* Header with progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{quiz.titel}</Typography>
            <Typography variant="body2" color="text.secondary">
              Frage {currentIndex + 1} von {totalQuestions}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {answeredCount} beantwortet
            </Typography>
            <Typography variant="caption" color="success.main">
              {score} richtig
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Question */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip 
              label={difficultyLabels[currentQuestion.schwierigkeit] || currentQuestion.schwierigkeit} 
              color={difficultyColors[currentQuestion.schwierigkeit] || 'default'}
              size="small"
            />
            <Chip 
              label={currentQuestion.typ.replace(/_/g, ' ')} 
              variant="outlined"
              size="small"
            />
          </Box>

          {/* Context for scenario questions */}
          {(currentQuestion.kontext || currentQuestion.szenario) && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(0, 180, 216, 0.1)' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                <strong>Szenario:</strong> {currentQuestion.kontext || currentQuestion.szenario}
              </Typography>
            </Paper>
          )}

          <Typography variant="h6" sx={{ mb: 3 }}>
            {getQuestionText(currentQuestion)}
          </Typography>

          {/* Answer options based on question type */}
          {currentQuestion.typ === 'richtig_falsch' ? (
            // True/False question
            <RadioGroup
              value={answers[currentQuestion.id]?.toString() || ''}
              onChange={(e) => handleSingleAnswer(currentQuestion.id, e.target.value)}
            >
              {[
                { id: 'true', text: 'Richtig' },
                { id: 'false', text: 'Falsch' },
              ].map((option) => {
                const isSubmitted = submitted[currentQuestion.id];
                const userSelected = answers[currentQuestion.id]?.toString() === option.id;
                const correctAnswer = currentQuestion.richtige_antwort;
                const isCorrectOption = (correctAnswer === true && option.id === 'true') || 
                                        (correctAnswer === false && option.id === 'false');

                return (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio disabled={isSubmitted} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.text}</span>
                        {isSubmitted && isCorrectOption && <CheckCircleIcon color="success" fontSize="small" />}
                        {isSubmitted && userSelected && !isCorrectOption && <CancelIcon color="error" fontSize="small" />}
                      </Box>
                    }
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: isSubmitted
                        ? isCorrectOption
                          ? 'rgba(16, 185, 129, 0.1)'
                          : userSelected
                            ? 'rgba(244, 63, 94, 0.1)'
                            : 'transparent'
                        : 'transparent',
                    }}
                  />
                );
              })}
            </RadioGroup>
          ) : isMultiSelect(currentQuestion) ? (
            // Multiple select question
            <FormGroup>
              {(currentQuestion.optionen || []).map((option) => {
                const isSubmitted = submitted[currentQuestion.id];
                const userSelected = ((answers[currentQuestion.id] as string[]) || []).includes(option.id);
                const correctAnswers = currentQuestion.richtige_antworten || [];
                const isCorrectOption = correctAnswers.includes(option.id);

                return (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        checked={userSelected}
                        onChange={(e) => handleMultipleAnswer(currentQuestion.id, option.id, e.target.checked)}
                        disabled={isSubmitted}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.text}</span>
                        {isSubmitted && isCorrectOption && <CheckCircleIcon color="success" fontSize="small" />}
                        {isSubmitted && userSelected && !isCorrectOption && <CancelIcon color="error" fontSize="small" />}
                      </Box>
                    }
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: isSubmitted
                        ? isCorrectOption
                          ? 'rgba(16, 185, 129, 0.1)'
                          : userSelected
                            ? 'rgba(244, 63, 94, 0.1)'
                            : 'transparent'
                        : 'transparent',
                    }}
                  />
                );
              })}
            </FormGroup>
          ) : (
            // Single choice question (default)
            <RadioGroup
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleSingleAnswer(currentQuestion.id, e.target.value)}
            >
              {(currentQuestion.optionen || []).map((option) => {
                const isSubmitted = submitted[currentQuestion.id];
                const userSelected = answers[currentQuestion.id] === option.id;
                const isCorrectOption = currentQuestion.richtige_antwort === option.id;

                return (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio disabled={isSubmitted} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.text}</span>
                        {isSubmitted && isCorrectOption && <CheckCircleIcon color="success" fontSize="small" />}
                        {isSubmitted && userSelected && !isCorrectOption && <CancelIcon color="error" fontSize="small" />}
                      </Box>
                    }
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: isSubmitted
                        ? isCorrectOption
                          ? 'rgba(16, 185, 129, 0.1)'
                          : userSelected
                            ? 'rgba(244, 63, 94, 0.1)'
                            : 'transparent'
                        : 'transparent',
                    }}
                  />
                );
              })}
            </RadioGroup>
          )}
        </Box>

        {/* Explanation (shown after submit) */}
        {submitted[currentQuestion.id] && (
          <Box sx={{ mb: 3 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                mb: 1,
              }}
              onClick={() => setShowExplanation(prev => ({ ...prev, [currentQuestion.id]: !prev[currentQuestion.id] }))}
            >
              <LightbulbIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2">Erklärung</Typography>
              <IconButton size="small">
                {showExplanation[currentQuestion.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={showExplanation[currentQuestion.id]}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(249, 115, 22, 0.1)' }}>
                <Typography variant="body2">
                  {currentQuestion.erklaerung}
                </Typography>
              </Paper>
            </Collapse>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            startIcon={<NavigateBeforeIcon />}
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            Zurück
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!submitted[currentQuestion.id] ? (
              <Button
                variant="contained"
                onClick={handleSubmitAnswer}
                disabled={answers[currentQuestion.id] === undefined}
              >
                Antwort prüfen
              </Button>
            ) : currentIndex === totalQuestions - 1 ? (
              <Button
                variant="contained"
                color="success"
                onClick={handleFinishQuiz}
                disabled={answeredCount < totalQuestions}
              >
                Quiz abschließen
              </Button>
            ) : null}
          </Box>

          <Button
            endIcon={<NavigateNextIcon />}
            onClick={handleNext}
            disabled={currentIndex === totalQuestions - 1}
          >
            Weiter
          </Button>
        </Box>

        {/* Quick navigation dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 3 }}>
          {quiz.fragen.map((q, idx) => (
            <Box
              key={q.id}
              onClick={() => setCurrentIndex(idx)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: submitted[q.id]
                  ? isCorrect(q.id)
                    ? 'success.main'
                    : 'error.main'
                  : idx === currentIndex
                    ? 'primary.main'
                    : 'rgba(255,255,255,0.3)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Training content display component
interface TrainingDisplayProps {
  schulung: Schulung;
}

export const TrainingDisplay: React.FC<TrainingDisplayProps> = ({ schulung }) => {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {schulung.titel}
      </Typography>

      {/* Meta info */}
      {(schulung.zielgruppe || schulung.gesamtdauer) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {schulung.zielgruppe && (
            <Chip label={`Zielgruppe: ${schulung.zielgruppe}`} variant="outlined" />
          )}
          {schulung.gesamtdauer && (
            <Chip label={`Dauer: ${schulung.gesamtdauer}`} color="primary" />
          )}
        </Box>
      )}

      {/* Learning objectives */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎯 Lernziele
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          {schulung.lernziele.map((lz, idx) => (
            <Box component="li" key={lz.id || idx} sx={{ mb: 1 }}>
              <Typography variant="body1">
                {lz.beschreibung}
                {lz.niveau && <Chip label={lz.niveau} size="small" sx={{ ml: 1 }} />}
              </Typography>
              {lz.messbar_durch && (
                <Typography variant="caption" color="text.secondary">
                  Messbar durch: {lz.messbar_durch}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Modules */}
      {schulung.module.map((modul, idx) => {
        const modulNr = modul.nr || modul.nummer || (idx + 1);
        const modulTitel = modul.name || modul.titel || '';
        const modulInhalte = modul.themen || modul.inhalte || [];
        
        return (
          <Paper key={modulNr} sx={{ p: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6">
                Modul {modulNr}: {modulTitel}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={modul.dauer} color="primary" size="small" />
                {modul.methode && <Chip label={modul.methode} variant="outlined" size="small" />}
              </Box>
            </Box>
            
            {modulInhalte.length > 0 && (
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                {modulInhalte.map((inhalt, i) => (
                  <Box component="li" key={i}>
                    <Typography variant="body2">{inhalt}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Exercise - support both formats */}
            {modul.uebung && (
              <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  💡 Praxisübung: {modul.uebung.titel}
                </Typography>
                {modul.uebung.szenario && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Szenario:</strong> {modul.uebung.szenario}
                  </Typography>
                )}
                {modul.uebung.aufgabe && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Aufgabe:</strong> {modul.uebung.aufgabe}
                  </Typography>
                )}
                {modul.uebung.erwartetes_ergebnis && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Erwartetes Ergebnis:</strong> {modul.uebung.erwartetes_ergebnis}
                  </Typography>
                )}
              </Alert>
            )}
            {modul.praxisuebung && !modul.uebung && (
              <Alert severity="info" icon={false}>
                <strong>💡 Praxisübung:</strong> {modul.praxisuebung}
              </Alert>
            )}
          </Paper>
        );
      })}

      {/* Materials */}
      {schulung.materialien && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            📚 Materialien
          </Typography>
          {schulung.materialien.praesentationen && schulung.materialien.praesentationen.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Präsentationen:</Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {schulung.materialien.praesentationen.map((p, i) => (
                  <Box component="li" key={i}><Typography variant="body2">{p}</Typography></Box>
                ))}
              </Box>
            </Box>
          )}
          {schulung.materialien.handouts && schulung.materialien.handouts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Handouts:</Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {schulung.materialien.handouts.map((h, i) => (
                  <Box component="li" key={i}><Typography variant="body2">{h}</Typography></Box>
                ))}
              </Box>
            </Box>
          )}
          {schulung.materialien.software && schulung.materialien.software.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Software:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {schulung.materialien.software.map((s, i) => (
                  <Chip key={i} label={s} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Assessment */}
      {schulung.assessment && (
        <Paper sx={{ p: 3, bgcolor: 'rgba(0, 180, 216, 0.1)' }}>
          <Typography variant="h6" gutterBottom>
            📋 Assessment
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Methode:</strong> {schulung.assessment.methode}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Kriterien:</strong> {schulung.assessment.kriterien.join(', ')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

// Helper function to parse training content from n8n response
export const parseTrainingContent = (content: string): TrainingContent | null => {
  try {
    // Try to parse as JSON directly
    const parsed = JSON.parse(content);
    if (parsed.schulung && parsed.quiz) {
      return parsed as TrainingContent;
    }
    return null;
  } catch {
    // Try to extract JSON from markdown code block
    const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[1]);
        if (parsed.schulung && parsed.quiz) {
          return parsed as TrainingContent;
        }
      } catch {
        return null;
      }
    }
    return null;
  }
};

export default QuizComponent;
